import { useState, type CSSProperties } from 'react';
import type { Vivier } from '../partage/api/types';
import { ChercherUneTache } from './ChercherUneTache';
import type { Pastille } from './ChoixDuDossier';
import { LigneAOuvrir } from './LigneAOuvrir';
import { PanneauDesReglages } from './PanneauDesReglages';
import { REGLAGES_PAR_DEFAUT, type ReglagesDeMaintenant } from './reglagesDeMaintenant';
import { HautDuPouce } from '../partage/HautDuPouce';
import { useFeuille } from '../partage/useArbre';
import { dureeLisible } from '../partage/datesEnMots';
import {
  AUTRE_CHOSE,
  C_EST_FAIT,
  cEstFaitPour,
  CHERCHER_UNE_AUTRE_TACHE,
  debloquantQuiLibere,
  ENSUITE,
  PENDANT_CE_TEMPS_DE_ENSUITE,
  MAINTENANT,
  REGLER_MAINTENANT,
  REVENIR_A_TOUT,
  RIEN_DE_FAISABLE,
  RIEN_DE_FAISABLE_TOUT_DE_SUITE,
} from '../partage/vocabulaire';
import { ligneDesEcarts } from './maintenantEnMots';
import { debloquantHorsDuTemps, estFiltre, resumeDesReglages } from './reglagesDuPouce';
import {
  aLaUne,
  debloquantSousLaUne,
  fileDeMaintenant,
  pourquoiCourt,
  raisonDeLaUne,
  type LigneALaUne,
} from './maintenantDuPouce';
import { teinteDuProjet } from '../apparence';
import './MaintenantAuPouce.css';
import { BandeauAdresse } from '../comptes';

/**
 * « Maintenant » au téléphone (chantier `telephone-cadre`, ticket 02, `design/telephone.html`,
 * piste « Maintenant », décisions d'Igor du 2026-10-05) : **une seule chose en grand**, avec son
 * pourquoi, son projet, « C'est fait » et « Autre chose ». Dessous, le débloquant, puis
 * « Ensuite » (deux lignes), puis « Chercher une autre tâche ».
 *
 * Prioritaire décide de ce qui est en grand : sa première ligne s'il y en a, sinon la première de
 * « Pendant ce temps ». « Autre chose » passe à la suivante dans cet ordre ; rien n'est enregistré,
 * l'ordre revient à la prochaine ouverture. Ni « Pour aujourd'hui », ni « Garder pour
 * aujourd'hui » : la journée a son onglet. « À vérifier », sous « Ensuite », sur son post-it
 * punaisé (chantier telephone-habit, 04, `AVerifierAuPouce`).
 *
 * **En haut, le haut du carnet** (chantier telephone-habit, 02, `HautDuPouce`) : l'initiale, le
 * résumé des réglages en cours au-dessus du titre, et le ⚙, qui ouvre la feuille des réglages
 * (projet, J'ai, Je suis, Courage ; ticket 03 de telephone-cadre, décision d'Igor du 2026-10-05,
 * revue 5) ; filtré, la feuille porte aussi « Revenir à tout ». Le débloquant qui ne tient pas
 * dans le temps choisi est dit en une ligne sous « Ensuite », au lieu de sa carte.
 *
 * Les règles sans rendu sont dans `maintenantDuPouce.ts` et `reglagesDuPouce.ts`. L'ordinateur
 * garde `Maintenant` tel quel.
 */
const teinte = (couleur: string | null | undefined): CSSProperties => ({ ['--teinte' as string]: teinteDuProjet(couleur) });

export function MaintenantAuPouce({
  vivier,
  reglages,
  dossier,
  occupe,
  onOuvrir,
  onFait,
  onRegler,
  aVerifier,
  couche = false,
}: {
  vivier: Vivier;
  reglages: ReglagesDeMaintenant;
  /** Le projet choisi, ou `null` : tous. */
  dossier: Pastille | null;
  /** Une coche est partie : « C'est fait » attend sa réponse. */
  occupe: boolean;
  onOuvrir: (id: string) => void;
  onFait: (ligne: LigneALaUne) => void;
  /** « Voir » du panneau, ou la croix : les réglages et le projet appliqués d'un coup. */
  onRegler: (reglages: ReglagesDeMaintenant, dossier: Pastille | null) => void;
  /** « À vérifier », le post-it punaisé, sous « Ensuite » (chantier telephone-habit, 04). */
  aVerifier?: React.ReactNode;
  /** Le téléphone couché : les mêmes morceaux en deux colonnes. */
  couche?: boolean;
}) {
  // « Autre chose » : le rang de la tâche en grand, gardé le temps de l'écran seulement.
  const [rang, setRang] = useState(0);
  const [cherche, setCherche] = useState(false);
  const projet = dossier?.id ?? null;

  if (cherche) {
    return (
      <ChercherUneTache
        reglages={reglages}
        projet={projet}
        onOuvrir={onOuvrir}
        onRetour={() => setCherche(false)}
      />
    );
  }

  const file = fileDeMaintenant(vivier);
  const { une, ensuite } = aLaUne(file, rang);
  const debloquant = debloquantSousLaUne(vivier.debloquants, une?.id ?? null);
  // Trop long pour le temps choisi : une ligne sous « Ensuite », pas de carte.
  const loin = debloquantHorsDuTemps(debloquant, vivier.reglages.temps, projet !== null);
  const ecart = debloquant ? ligneDesEcarts(debloquant.ecarts, vivier.reglages.temps) : null;
  // Ce que tes réglages cachent se cherche aussi : le bouton n'est jamais mort.
  const reglesPosees = reglages.temps !== null || reglages.lieu !== null;
  const filtre = estFiltre(reglages, projet);
  const resume = resumeDesReglages(reglages, dossier?.titre ?? null);

  const ouvrirLaRecherche = () => {
    setCherche(true);
    window.scrollTo({ top: 0 });
  };
  const regler = (r: ReglagesDeMaintenant, d: Pastille | null) => {
    // D'autres réglages, une autre file : la première revient en grand.
    setRang(0);
    onRegler(r, d);
  };

  const haut = (
    <HautDuPouce
      titre={MAINTENANT}
      couche={couche}
      resume={resume}
      reglages={{
        titre: REGLER_MAINTENANT,
        tuto: 'bouton-filtrer',
        tutoResume: 'pastille-dossiers',
        contenu: (fermer) => (
          <>
            <PanneauDesReglages
              reglages={reglages}
              projet={dossier}
              pastilles={vivier.pastilles}
              onVoir={(r, d) => {
                fermer();
                regler(r, d);
              }}
            />
            {filtre && (
              <button
                type="button"
                className="pouce-ligne-reg"
                onClick={() => {
                  fermer();
                  regler(REGLAGES_PAR_DEFAUT, null);
                }}
              >
                {REVENIR_A_TOUT}
                <span aria-hidden="true">×</span>
              </button>
            )}
          </>
        ),
      }}
    />
  );

  /*
   * Les pièces du carnet (chantier telephone-habit, 04, maquette `maintenant()`) : la chose à
   * faire sur une fiche scotchée, au ruban de son projet ; le débloquant en encart vert ;
   * « Ensuite » écrit à la main, ses lignes dans un paquet, chacune avec sa case.
   */
  const laUne = une ? (
    <section className="une" aria-label={une.titre} style={teinte(une.dossier?.couleur)}>
      <span className="une-scotch" aria-hidden="true" />
      <span className="une-ruban" aria-hidden="true" />
      {une.dossier && (
        <p className="une-projet">
          <i className="une-carre" aria-hidden="true" />
          {une.dossier.titre}
        </p>
      )}
      <button type="button" className="une-titre" onClick={() => onOuvrir(une.id)}>
        {une.titre}
      </button>
      <p className="une-raison">{raisonDeLaUne(une)}</p>
      <MetaDeLaUne id={une.id} effortMin={une.effortMin} />
      <div className="une-gestes">
        <button type="button" className="une-fait" disabled={occupe} onClick={() => onFait(une)}>
          {C_EST_FAIT}
        </button>
        {file.length > 1 && (
          <button type="button" className="une-autre" onClick={() => setRang((r) => (r + 1) % file.length)}>
            {AUTRE_CHOSE}
          </button>
        )}
      </div>
    </section>
  ) : (
    <p className="une-vide">{reglesPosees ? RIEN_DE_FAISABLE : RIEN_DE_FAISABLE_TOUT_DE_SUITE}</p>
  );

  const leDebloquant = debloquant && !loin && (
    <button type="button" className="une-debloquant" onClick={() => onOuvrir(debloquant.bulle.id)}>
      <span className="une-debloquant-signe" aria-hidden="true">
        🔓
      </span>
      <span className="une-debloquant-texte">
        <span className="une-debloquant-titre">{debloquant.bulle.titre}</span>
        <span className="une-debloquant-quoi">{debloquantQuiLibere(debloquant.libere.length)}</span>
        {ecart && <span className="une-debloquant-ecart">{ecart}</span>}
      </span>
    </button>
  );

  const lEnsuite = ensuite.length > 0 && (
    <section className="ensuite">
      <h2 className="une-etiquette">
        {ENSUITE} <small>{PENDANT_CE_TEMPS_DE_ENSUITE}</small>
      </h2>
      <ul className="ensuite-lignes">
        {ensuite.map((l) => (
          <li key={l.id} className="ensuite-ligne">
            <button
              type="button"
              className="ensuite-case"
              aria-label={cEstFaitPour(l.titre)}
              disabled={occupe}
              onClick={() => onFait(l)}
            />
            <LigneAOuvrir titre={l.titre} pourquoi={pourquoiCourt(l)} prioritaire={l.prioritaire} onOuvrir={() => onOuvrir(l.id)} />
          </li>
        ))}
      </ul>
    </section>
  );

  const leLoin = loin && <p className="une-debloquant-loin">{loin}</p>;
  const chercher = (une || reglesPosees) && (
    <button type="button" className="autre-tache-ouvrir" onClick={ouvrirLaRecherche}>
      {CHERCHER_UNE_AUTRE_TACHE} ›
    </button>
  );
  // Au téléphone, pas d'accueil : le bandeau des comptes d'avant le chantier mails se pose ici.
  const bandeau = <BandeauAdresse />;

  /*
   * Couché (chantier telephone-couche-suite, 01, maquette `cMaintenant()`, `.c2`) : les mêmes
   * morceaux en deux colonnes, la chose à faire et le débloquant à gauche, Ensuite et À vérifier
   * à droite ; chaque colonne défile seule si elle dépasse.
   */
  if (couche) {
    return (
      <div className="pouce-maintenant pouce-maintenant--couche">
        {haut}
        <div className="pouce-maintenant-colonnes">
          <div className="pouce-maintenant-colonne">
            {bandeau}
            {laUne}
            {leDebloquant}
            {leLoin}
          </div>
          <div className="pouce-maintenant-colonne">
            {lEnsuite}
            {aVerifier}
            {chercher}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pouce-maintenant">
      {haut}
      {bandeau}
      {laUne}
      {leDebloquant}
      {lEnsuite}
      {aVerifier}
      {leLoin}
      {chercher}
    </div>
  );
}

/**
 * Sous la raison, la durée et le lieu de la chose à faire (chantier telephone-ecrans, 03,
 * maquette `maintenant()`, `.fichec .meta`) : « ⏱ 45 min · 📍 Leroy Merlin », chacun seulement
 * s'il est posé. La durée vient de la ligne de « Maintenant », le lieu de la bulle telle que le
 * serveur la rend (sa feuille, souvent déjà lue) ; rien ne s'invente.
 */
function MetaDeLaUne({ id, effortMin }: { id: string; effortMin: number | null }) {
  const feuille = useFeuille(id);
  const lieu = feuille.data?.bulle.id === id ? feuille.data.bulle.lieuTexte?.trim() || null : null;
  if (effortMin == null && !lieu) return null;
  return (
    <p className="une-meta">
      {effortMin != null && <span>⏱ {dureeLisible(effortMin)}</span>}
      {lieu && <span className="une-meta-lieu">📍 {lieu}</span>}
    </p>
  );
}
